import { CbhIcon, Divider, DotSeparatedList, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { formatDistanceInMiles } from "@src/appV2/lib/utils/distance";
import { useOpenGeoLocation } from "@src/appV2/redesign/OpenGeoLocation/useOpenGeoLocation";

import { type PlacementWithDetails } from "../../../api/useFetchPaginatedPlacements";
import { type AlternativeAddressData } from "../hooks/useAlternativeAddress";
import { PlacementSectionTemplate } from "./PlacementSectionTemplate";

interface AlternativeAddressSectionProps {
  workerId: string;
  placementId: string;
  placementCandidateId: string;
  hiringEvents: PlacementWithDetails["hiringEvents"];
  alternativeAddressData: AlternativeAddressData;
}

export function AlternativeAddressSection(props: AlternativeAddressSectionProps) {
  const { workerId, placementId, placementCandidateId, hiringEvents, alternativeAddressData } =
    props;

  const { coordinates, formattedAddress, distance } = alternativeAddressData;

  const { googleMapsHref } = useOpenGeoLocation({
    location: {
      lat: coordinates.latitude,
      lng: coordinates.longitude,
    },
    formattedAddress,
  });

  if (!isDefined(googleMapsHref)) {
    return null;
  }

  return (
    <>
      <Divider color="primary" />
      <Stack spacing={5}>
        <PlacementSectionTemplate title="Interview location" iconType="map-paper">
          {isDefined(distance) && (
            <DotSeparatedList
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              {isDefined(formattedAddress) && (
                <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
                  {formattedAddress}
                </Text>
              )}
              <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
                {formatDistanceInMiles(distance, "long")}
              </Text>
              <Box
                onClick={() => {
                  logEvent(APP_V2_APP_EVENTS.PLACEMENT_DETAILS_PAGE_ACTIONS, {
                    action: "interview_location_open_in_maps",
                    placementId,
                    placementCandidateId,
                    source: placementCandidateId ? "jobs" : "bookings",
                    workerId,
                    hiringEvents: hiringEvents?.map((event) => {
                      return {
                        id: event.id,
                        name: event.attributes.type,
                        startDate: event.attributes.fixedSchedule?.startDate,
                        endDate: event.attributes.fixedSchedule?.endDate,
                      };
                    }),
                  });

                  window.open(googleMapsHref, "_blank");
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    paddingBottom: 1,
                    borderBottom: (theme) => `1px solid ${theme.palette.border?.primary}`,
                  }}
                >
                  <CbhIcon type="map-pin" size="small" />
                  <Text variant="body2">Open in map</Text>
                </Stack>
              </Box>
            </DotSeparatedList>
          )}
        </PlacementSectionTemplate>
      </Stack>
    </>
  );
}
